import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, Lock, Unlock, AlertTriangle, Users, Activity, 
  Settings, RefreshCw, Check, X, Clock, Globe, Eye, 
  Ban, CheckCircle, XCircle, TrendingUp, AlertCircle,
  Key, Trash2, Search, ChevronRight, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import { getIdleTimeoutSettings, saveIdleTimeoutSettings } from '../../hooks/useIdleTimeout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [blacklistedIPs, setBlacklistedIPs] = useState([]);
  const [savingPolicy, setSavingPolicy] = useState(false);
  
  // Filter states
  const [historyFilter, setHistoryFilter] = useState({ email: '', success: '' });
  const [newBlacklistIP, setNewBlacklistIP] = useState({ ip: '', reason: '' });
  
  // Idle timeout settings (Session Security)
  const [idleSettings, setIdleSettings] = useState(getIdleTimeoutSettings);

  useEffect(() => {
    loadData();
  }, []);

  // Sync idle settings when they change externally
  useEffect(() => {
    const handleSettingsChange = (event) => {
      setIdleSettings(event.detail || getIdleTimeoutSettings());
    };
    window.addEventListener('idle-timeout-settings-changed', handleSettingsChange);
    return () => window.removeEventListener('idle-timeout-settings-changed', handleSettingsChange);
  }, []);

  const updateIdleSettings = (updates) => {
    const newSettings = saveIdleTimeoutSettings(updates);
    setIdleSettings(newSettings);
    toast.success('Session security settings updated');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, lockedRes, alertsRes, policyRes, blacklistRes] = await Promise.all([
        axios.get(`${API_URL}/api/security-admin/analytics`),
        axios.get(`${API_URL}/api/security-admin/locked-accounts`),
        axios.get(`${API_URL}/api/security-admin/alerts?is_resolved=false&limit=20`),
        axios.get(`${API_URL}/api/security-admin/policy`),
        axios.get(`${API_URL}/api/security-admin/blacklisted-ips`)
      ]);
      
      setAnalytics(analyticsRes.data);
      setLockedAccounts(lockedRes.data);
      setAlerts(alertsRes.data);
      setPolicy(policyRes.data);
      setBlacklistedIPs(blacklistRes.data);
    } catch (error) {
      console.error('Error loading security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const loadLoginHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (historyFilter.email) params.append('email', historyFilter.email);
      if (historyFilter.success !== '') params.append('success', historyFilter.success);
      params.append('limit', '100');
      
      const response = await axios.get(`${API_URL}/api/security-admin/login-history?${params.toString()}`);
      setLoginHistory(response.data);
    } catch (error) {
      console.error('Error loading login history:', error);
      toast.error('Failed to load login history');
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadLoginHistory();
    }
  }, [activeTab, historyFilter]);

  const handleUnlockAccount = async (email) => {
    try {
      await axios.post(`${API_URL}/api/security-admin/unlock-account?email=${encodeURIComponent(email)}`);
      toast.success(`Account ${email} unlocked successfully`);
      loadData();
    } catch (error) {
      console.error('Error unlocking account:', error);
      toast.error('Failed to unlock account');
    }
  };

  const handleForcePasswordReset = async (email) => {
    try {
      await axios.post(`${API_URL}/api/security-admin/force-password-reset?email=${encodeURIComponent(email)}`);
      toast.success(`Password reset required for ${email}`);
    } catch (error) {
      console.error('Error forcing password reset:', error);
      toast.error('Failed to force password reset');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await axios.post(`${API_URL}/api/security-admin/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      loadData();
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error('Failed to resolve alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API_URL}/api/security-admin/alerts/${alertId}`);
      toast.success('Alert deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
  };

  const handleSavePolicy = async () => {
    try {
      setSavingPolicy(true);
      await axios.put(`${API_URL}/api/security-admin/policy`, policy);
      toast.success('Security policy updated successfully');
    } catch (error) {
      console.error('Error saving policy:', error);
      toast.error('Failed to update security policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleBlacklistIP = async () => {
    if (!newBlacklistIP.ip || !newBlacklistIP.reason) {
      toast.error('Please provide IP address and reason');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/security-admin/blacklist-ip?ip_address=${encodeURIComponent(newBlacklistIP.ip)}&reason=${encodeURIComponent(newBlacklistIP.reason)}`);
      toast.success(`IP ${newBlacklistIP.ip} blacklisted`);
      setNewBlacklistIP({ ip: '', reason: '' });
      loadData();
    } catch (error) {
      console.error('Error blacklisting IP:', error);
      toast.error('Failed to blacklist IP');
    }
  };

  const handleRemoveBlacklistIP = async (ip) => {
    try {
      await axios.delete(`${API_URL}/api/security-admin/blacklist-ip/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} removed from blacklist`);
      loadData();
    } catch (error) {
      console.error('Error removing IP from blacklist:', error);
      toast.error('Failed to remove IP from blacklist');
    }
  };

  const handleCheckSuspiciousActivity = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/security-admin/check-suspicious-activity`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      console.error('Error checking suspicious activity:', error);
      toast.error('Failed to check suspicious activity');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityBadge = (severity) => {
    const config = {
      critical: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
      warning: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
      info: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: AlertCircle }
    };
    const cfg = config[severity] || config.info;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {severity}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-600" />
            Security Management
          </h1>
          <p className="text-gray-600 mt-1">Monitor and manage platform security settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCheckSuspiciousActivity}>
            <Activity className="h-4 w-4 mr-2" />
            Scan for Threats
          </Button>
          <Button onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Logins (24h)</p>
                  <p className="text-2xl font-bold text-green-600">{analytics.total_logins_24h}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Failed Logins (24h)</p>
                  <p className="text-2xl font-bold text-red-600">{analytics.failed_logins_24h}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Locked Accounts</p>
                  <p className="text-2xl font-bold text-yellow-600">{analytics.locked_accounts_count}</p>
                </div>
                <Lock className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Active Alerts</p>
                  <p className="text-2xl font-bold text-orange-600">{analytics.suspicious_activities_count}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Activity className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="lockouts" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Lock className="h-4 w-4 mr-2" />
            Lockouts
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Alerts ({alerts.length})
          </TabsTrigger>
          <TabsTrigger value="session" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Timer className="h-4 w-4 mr-2" />
            Session
          </TabsTrigger>
          <TabsTrigger value="policy" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Settings className="h-4 w-4 mr-2" />
            Policy
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Clock className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="blacklist" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Ban className="h-4 w-4 mr-2" />
            IP Blacklist
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Top Failed IPs */}
          {analytics?.top_failed_ips?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-red-600" />
                  Top Failed Login IPs (24h)
                </CardTitle>
                <CardDescription>IP addresses with the most failed login attempts</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Failed Attempts</TableHead>
                      <TableHead>Risk Level</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.top_failed_ips.slice(0, 5).map((item) => (
                      <TableRow key={item.ip}>
                        <TableCell className="font-mono">{item.ip}</TableCell>
                        <TableCell>
                          <Badge variant={item.count >= 10 ? "destructive" : "secondary"}>
                            {item.count} attempts
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.count >= 20 ? (
                            <Badge className="bg-red-100 text-red-800">Critical</Badge>
                          ) : item.count >= 10 ? (
                            <Badge className="bg-yellow-100 text-yellow-800">High</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">Medium</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setNewBlacklistIP({ ip: item.ip, reason: 'Multiple failed login attempts' });
                              setActiveTab('blacklist');
                            }}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            Blacklist
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Recent Alerts Summary */}
          {alerts.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-800 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Active Security Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {alerts.slice(0, 3).map((alert) => (
                    <div key={alert.id} className="bg-white p-3 rounded-lg border border-red-200 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{alert.title}</p>
                        <p className="text-sm text-gray-600">{alert.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getSeverityBadge(alert.severity)}
                        <Button size="sm" variant="outline" onClick={() => handleResolveAlert(alert.id)}>
                          <Check className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {alerts.length > 3 && (
                    <Button variant="ghost" className="w-full" onClick={() => setActiveTab('alerts')}>
                      View all {alerts.length} alerts
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-teal-600" />
                  Login Activity (Last 24h)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="h-4 w-4" />
                  <span>{analytics?.unique_ips_24h || 0} unique IP addresses</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <Globe className="h-4 w-4" />
                  <span>{analytics?.new_devices_24h || 0} unique devices/browsers</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-teal-600" />
                  Security Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Account Lockout</span>
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">2FA Available</span>
                    <Badge className="bg-green-100 text-green-800">Enabled</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">IP Blacklist</span>
                    <Badge variant="secondary">{blacklistedIPs.length} IPs</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Lockouts Tab */}
        <TabsContent value="lockouts">
          <Card>
            <CardHeader>
              <CardTitle>Locked Accounts</CardTitle>
              <CardDescription>Accounts locked due to failed login attempts</CardDescription>
            </CardHeader>
            <CardContent>
              {lockedAccounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Lock className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No locked accounts</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Failed Attempts</TableHead>
                      <TableHead>Locked Until</TableHead>
                      <TableHead>Last Attempt</TableHead>
                      <TableHead>Source IPs</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lockedAccounts.map((account) => (
                      <TableRow key={account.email}>
                        <TableCell className="font-medium">{account.email}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">{account.failed_attempts}</Badge>
                        </TableCell>
                        <TableCell>{formatTimestamp(account.locked_until)}</TableCell>
                        <TableCell>{formatTimestamp(account.last_attempt)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {account.ip_addresses?.slice(0, 3).map((ip) => (
                              <Badge key={ip} variant="outline" className="font-mono text-xs">{ip}</Badge>
                            ))}
                            {account.ip_addresses?.length > 3 && (
                              <Badge variant="outline">+{account.ip_addresses.length - 3}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleUnlockAccount(account.email)}>
                              <Unlock className="h-4 w-4 mr-1" />
                              Unlock
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleForcePasswordReset(account.email)}>
                              <Key className="h-4 w-4 mr-1" />
                              Reset Password
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle>Security Alerts</CardTitle>
              <CardDescription>Active security alerts requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-2" />
                  <p>No active security alerts</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getSeverityBadge(alert.severity)}
                            <Badge variant="outline">{alert.alert_type}</Badge>
                          </div>
                          <h4 className="font-medium text-gray-900">{alert.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimestamp(alert.created_at)}
                            </span>
                            {alert.ip_address && (
                              <span className="flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {alert.ip_address}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button size="sm" variant="outline" onClick={() => handleResolveAlert(alert.id)}>
                            <Check className="h-4 w-4 mr-1" />
                            Resolve
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteAlert(alert.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Session Security Tab */}
        <TabsContent value="session">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-red-600" />
                Session Security
              </CardTitle>
              <CardDescription>Configure auto sign-out behavior for idle users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Auto Sign-Out */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="idle-timeout-enabled" className="font-medium flex items-center gap-2">
                      <Shield className="h-4 w-4 text-teal-600" />
                      Auto sign-out after inactivity
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">
                      Automatically sign out users when inactive for security
                    </p>
                  </div>
                  <Switch
                    id="idle-timeout-enabled"
                    checked={idleSettings.enabled}
                    onCheckedChange={(checked) => updateIdleSettings({ enabled: checked })}
                    data-testid="superadmin-idle-timeout-toggle"
                  />
                </div>
              </div>

              {/* Timeout Duration Selector */}
              {idleSettings.enabled && (
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-teal-600" />
                        Inactivity timeout
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Time before showing sign-out warning
                      </p>
                    </div>
                    <Select
                      value={String(idleSettings.timeoutMinutes)}
                      onValueChange={(value) => updateIdleSettings({ timeoutMinutes: parseInt(value) })}
                    >
                      <SelectTrigger className="w-[160px]" data-testid="superadmin-idle-timeout-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 minute (Test)</SelectItem>
                        <SelectItem value="5">5 minutes</SelectItem>
                        <SelectItem value="10">10 minutes</SelectItem>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>How it works:</strong> Users will see a 60-second warning countdown before being signed out. 
                      Any activity (clicks, key presses, scrolling) will reset the timer.
                    </p>
                  </div>

                  {idleSettings.timeoutMinutes === 1 && (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <AlertCircle className="h-4 w-4" />
                      <span>1-minute timeout is for testing only. Choose a longer duration for production use.</span>
                    </div>
                  )}
                </div>
              )}

              {/* Security Note */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-gray-600" />
                  Important Notes
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• This setting applies to your Super Admin session only</li>
                  <li>• For platform-wide session policies, use the Policy tab</li>
                  <li>• Settings are stored in your browser and persist across sessions</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Policy Tab */}
        <TabsContent value="policy">
          <Card>
            <CardHeader>
              <CardTitle>Security Policy Settings</CardTitle>
              <CardDescription>Configure platform-wide security policies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {policy && (
                <>
                  {/* Lockout Settings */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Lock className="h-5 w-5 text-teal-600" />
                      Account Lockout Policy
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Max Failed Attempts</Label>
                        <Input
                          type="number"
                          value={policy.max_failed_attempts}
                          onChange={(e) => setPolicy({ ...policy, max_failed_attempts: parseInt(e.target.value) })}
                          min={3}
                          max={20}
                        />
                        <p className="text-xs text-gray-500 mt-1">Lock account after this many failed attempts</p>
                      </div>
                      <div>
                        <Label>Lockout Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={policy.lockout_duration_minutes}
                          onChange={(e) => setPolicy({ ...policy, lockout_duration_minutes: parseInt(e.target.value) })}
                          min={5}
                          max={1440}
                        />
                        <p className="text-xs text-gray-500 mt-1">How long accounts remain locked</p>
                      </div>
                    </div>
                  </div>

                  {/* Session Settings */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Clock className="h-5 w-5 text-teal-600" />
                      Session Settings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Session Timeout (minutes)</Label>
                        <Input
                          type="number"
                          value={policy.session_timeout_minutes}
                          onChange={(e) => setPolicy({ ...policy, session_timeout_minutes: parseInt(e.target.value) })}
                          min={15}
                          max={480}
                        />
                        <p className="text-xs text-gray-500 mt-1">Auto-logout after inactivity</p>
                      </div>
                      <div>
                        <Label>Password Expiry (days)</Label>
                        <Input
                          type="number"
                          value={policy.password_expiry_days}
                          onChange={(e) => setPolicy({ ...policy, password_expiry_days: parseInt(e.target.value) })}
                          min={0}
                          max={365}
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = no expiry</p>
                      </div>
                    </div>
                  </div>

                  {/* Alert Settings */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-teal-600" />
                      Alert Settings
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Alert on Suspicious Login</Label>
                          <p className="text-xs text-gray-500">Create alert for unusual login patterns</p>
                        </div>
                        <Switch
                          checked={policy.alert_on_suspicious_login}
                          onCheckedChange={(checked) => setPolicy({ ...policy, alert_on_suspicious_login: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Alert on New Device</Label>
                          <p className="text-xs text-gray-500">Create alert when user logs in from new device</p>
                        </div>
                        <Switch
                          checked={policy.alert_on_new_device}
                          onCheckedChange={(checked) => setPolicy({ ...policy, alert_on_new_device: checked })}
                        />
                      </div>
                      <div>
                        <Label>Alert Threshold (failed logins/hour)</Label>
                        <Input
                          type="number"
                          value={policy.alert_threshold_failed_logins}
                          onChange={(e) => setPolicy({ ...policy, alert_threshold_failed_logins: parseInt(e.target.value) })}
                          min={3}
                          max={50}
                        />
                        <p className="text-xs text-gray-500 mt-1">Create alert when IP exceeds this many failed attempts per hour</p>
                      </div>
                    </div>
                  </div>

                  {/* 2FA Settings */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Shield className="h-5 w-5 text-teal-600" />
                      Two-Factor Authentication
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Require 2FA for Admins</Label>
                        <p className="text-xs text-gray-500">Force admin users to enable 2FA</p>
                      </div>
                      <Switch
                        checked={policy.require_2fa_for_admins}
                        onCheckedChange={(checked) => setPolicy({ ...policy, require_2fa_for_admins: checked })}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSavePolicy} disabled={savingPolicy}>
                      {savingPolicy ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Save Policy
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Login History</CardTitle>
              <CardDescription>Recent login attempts across the platform</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by email..."
                      className="pl-10"
                      value={historyFilter.email}
                      onChange={(e) => setHistoryFilter({ ...historyFilter, email: e.target.value })}
                    />
                  </div>
                </div>
                <select
                  className="border rounded-md px-3 py-2"
                  value={historyFilter.success}
                  onChange={(e) => setHistoryFilter({ ...historyFilter, success: e.target.value })}
                >
                  <option value="">All Attempts</option>
                  <option value="true">Successful Only</option>
                  <option value="false">Failed Only</option>
                </select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Device/Browser</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistory.map((entry, index) => (
                    <TableRow key={index} className={!entry.success ? 'bg-red-50' : ''}>
                      <TableCell className="text-sm">{formatTimestamp(entry.timestamp)}</TableCell>
                      <TableCell className="font-medium">{entry.email}</TableCell>
                      <TableCell>
                        {entry.success ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Success
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{entry.ip_address}</TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-[200px] truncate" title={entry.user_agent}>
                        {entry.user_agent || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blacklist Tab */}
        <TabsContent value="blacklist">
          <Card>
            <CardHeader>
              <CardTitle>IP Blacklist</CardTitle>
              <CardDescription>Manage blocked IP addresses</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Add IP Form */}
              <div className="border rounded-lg p-4 mb-6 bg-gray-50">
                <h4 className="font-medium mb-3">Add IP to Blacklist</h4>
                <div className="flex gap-3">
                  <Input
                    placeholder="IP Address (e.g., 192.168.1.1)"
                    value={newBlacklistIP.ip}
                    onChange={(e) => setNewBlacklistIP({ ...newBlacklistIP, ip: e.target.value })}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Reason for blocking"
                    value={newBlacklistIP.reason}
                    onChange={(e) => setNewBlacklistIP({ ...newBlacklistIP, reason: e.target.value })}
                    className="flex-1"
                  />
                  <Button onClick={handleBlacklistIP}>
                    <Ban className="h-4 w-4 mr-2" />
                    Block IP
                  </Button>
                </div>
              </div>

              {/* Blacklisted IPs List */}
              {blacklistedIPs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Globe className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No blacklisted IPs</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blacklistedIPs.map((item) => (
                      <TableRow key={item.ip_address}>
                        <TableCell className="font-mono">{item.ip_address}</TableCell>
                        <TableCell>{item.reason}</TableCell>
                        <TableCell>{formatTimestamp(item.added_at)}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleRemoveBlacklistIP(item.ip_address)}
                          >
                            <Trash2 className="h-4 w-4 mr-1 text-red-500" />
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityManagement;
